import React, { useEffect, useState } from "react";
import { api, getErr } from "@/lib/api";
import { toast } from "sonner";
import { X, Clock, Phone, ChatText, EnvelopeSimple, NotePencil, MapPin } from "@phosphor-icons/react";
import AiReplyDrafter from "@/components/crm/AiReplyDrafter";
import {
  CRM_STAGES, STAGE_MAP, ADMIN_ONLY_STAGES, LOSS_REASONS,
  SERVICE_TYPES, serviceLabel, sourceLabel,
} from "@/lib/crm";

const ACTIVITY_TYPES = [
  { key: "call", label: "Call", icon: Phone },
  { key: "sms", label: "SMS", icon: ChatText },
  { key: "email", label: "Email", icon: EnvelopeSimple },
  { key: "note", label: "Note", icon: NotePencil },
  { key: "site_visit", label: "Site visit", icon: MapPin },
];

function activityText(a) {
  const d = a.detail || {};
  if (d.text) return d.text;
  if (d.from || d.to) return `${(d.from || "?").replace(/_/g, " ")} → ${(d.to || "?").replace(/_/g, " ")}${d.note ? ` — ${d.note}` : ""}`;
  if (d.to_name) return `Assigned to ${d.to_name}`;
  if (d.fields) return `Edited: ${d.fields.join(", ")}`;
  return (a.kind || "").replace(/_/g, " ");
}

function StageBadge({ stage }) {
  const s = STAGE_MAP[stage] || STAGE_MAP.new;
  return (
    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white" style={{ backgroundColor: s.color }}>
      {s.label}
    </span>
  );
}

// Slide-over lead workspace shared by Admin (/ops/crm) and VA (/va/crm).
export default function CrmLeadDrawer({ leadId, role, isOwner, vas = [], onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState("");
  const [pendingStage, setPendingStage] = useState(null);
  const [note, setNote] = useState("");
  const [lossReason, setLossReason] = useState("price");
  const [lossNote, setLossNote] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);
  const [actType, setActType] = useState("call");
  const [actDetail, setActDetail] = useState("");
  const [quoteAmount, setQuoteAmount] = useState("");
  const [quoteDesc, setQuoteDesc] = useState("");
  const [econ, setEcon] = useState({ job_value: "", job_profit: "" });
  const [econNote, setEconNote] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/crm/leads/${leadId}`);
      setDetail(data);
      setNextAction(data.lead.next_action_date || "");
      setEdit({
        est_value: data.lead.est_value ?? "",
        prospect_address: data.lead.prospect_address || "",
        service_type: data.lead.service_type || "unknown",
      });
      setEcon({
        job_value: data.lead.job_value ?? "",
        job_profit: data.lead.job_profit ?? "",
      });
    } catch (e) {
      setErr(getErr(e));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  const lead = detail?.lead;
  const isAdmin = role === "admin";

  const saveFields = async () => {
    setBusy(true);
    try {
      await api.patch(`/crm/leads/${leadId}`, {
        est_value: edit.est_value === "" ? null : Number(edit.est_value),
        prospect_address: edit.prospect_address,
        service_type: edit.service_type,
        next_action_date: nextAction || null,
      });
      toast.success("Lead updated");
      await load();
      onChanged?.();
    } catch (e) { toast.error(getErr(e)); } finally { setBusy(false); }
  };

  const saveEconomics = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/crm/leads/${leadId}/economics`, {
        job_value: econ.job_value === "" ? null : Number(econ.job_value),
        job_profit: econ.job_profit === "" ? null : Number(econ.job_profit),
      });
      setEconNote(data.commission);
      toast.success(data.commission
        ? `Commission recalculated — pool $${Number(data.commission.pool_amount || 0).toFixed(2)}, agent $${Number(data.commission.amount || 0).toFixed(2)}`
        : "Economics saved");
      await load();
      onChanged?.();
    } catch (e) { toast.error(getErr(e)); } finally { setBusy(false); }
  };

  const moveStage = async (stage) => {
    setBusy(true);
    try {
      const body = { stage, note: note.trim() || null };
      if (stage === "lost") { body.loss_reason = lossReason; body.loss_note = lossNote.trim() || null; }
      if (nextAction) body.next_action_date = nextAction;
      await api.post(`/crm/leads/${leadId}/stage`, body);
      toast.success(`Moved to ${STAGE_MAP[stage].label}`);
      setPendingStage(null); setNote(""); setLossNote("");
      await load();
      onChanged?.();
    } catch (e) { toast.error(getErr(e)); } finally { setBusy(false); }
  };

  const assign = async (vaUserId) => {
    if (!vaUserId) return;
    try {
      await api.post(`/crm/leads/${leadId}/assign`, { va_user_id: vaUserId });
      toast.success("Lead assigned");
      await load(); onChanged?.();
    } catch (e) { toast.error(getErr(e)); }
  };

  const overrideAttribution = async (vaUserId) => {
    if (!vaUserId) return;
    const reason = window.prompt("Owner attribution override — enter the reason (audit-logged):");
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/crm/leads/${leadId}/attribution-override`, { va_user_id: vaUserId, reason: reason.trim() });
      toast.success("Attribution overridden (logged)");
      await load(); onChanged?.();
    } catch (e) { toast.error(getErr(e)); }
  };

  const logActivity = async () => {
    if (!actDetail.trim()) { toast.error("Describe the activity first"); return; }
    setBusy(true);
    try {
      await api.post(`/crm/leads/${leadId}/activity`, { type: actType, detail: actDetail.trim() });
      toast.success("Activity logged");
      setActDetail("");
      await load();
      onChanged?.();
    } catch (e) { toast.error(getErr(e)); } finally { setBusy(false); }
  };

  const createQuote = async () => {
    if (!quoteAmount || Number(quoteAmount) <= 0) { toast.error("Enter the quote amount"); return; }
    setBusy(true);
    try {
      await api.post(`/crm/leads/${leadId}/quotes`, {
        amount: Number(quoteAmount), description: quoteDesc.trim() || null,
      });
      toast.success("Quote recorded — lead moved to Quote Sent");
      setQuoteAmount(""); setQuoteDesc("");
      await load(); onChanged?.();
    } catch (e) { toast.error(getErr(e)); } finally { setBusy(false); }
  };

  const setQuoteStatus = async (quoteId, status) => {
    if (status === "accepted" && !window.confirm("Accept this quote? The lead becomes WON, a commission accrues, and a Project shell is created.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/crm/quotes/${quoteId}/status`, { status });
      if (status === "accepted") {
        toast.success(data.project_id ? "Quote accepted — lead WON, project created" : "Quote accepted — lead WON");
      } else {
        toast.success("Quote marked declined");
      }
      await load(); onChanged?.();
    } catch (e) { toast.error(getErr(e)); } finally { setBusy(false); }
  };

  const stageTargets = CRM_STAGES.filter((s) => {
    if (!lead || s.key === lead.crm_stage) return false;
    if (!isAdmin && ADMIN_ONLY_STAGES.includes(s.key)) return false;
    return true;
  });

  return (
    <div className="fixed inset-0 z-[90] flex justify-end bg-black/40" onClick={onClose} data-testid="crm-lead-drawer">
      <div
        className="flex h-full w-full max-w-lg flex-col overflow-y-auto bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-[#E5E7EB] bg-white p-4">
          <div>
            <div className="font-mono-label">Lead</div>
            <div className="font-display text-xl font-black">{lead?.prospect_name || "…"}</div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {lead && <StageBadge stage={lead.crm_stage} />}
              {lead && (
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#4B5563]">
                  {sourceLabel(lead.source)}
                </span>
              )}
              {lead?.overdue && (
                <span className="bg-red-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white" data-testid="crm-drawer-overdue-chip">
                  Overdue
                </span>
              )}
              {lead?.sla_state === "breached" && (
                <span className="bg-red-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white" data-testid="crm-drawer-sla-chip">
                  SLA Breached
                </span>
              )}
              {lead?.sla_state === "warning" && (
                <span className="bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white" data-testid="crm-drawer-sla-chip">
                  SLA Warning
                </span>
              )}
              {lead?.sla_state === "running" && (
                <span className="border border-[#0044FF] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#0044FF]" data-testid="crm-drawer-sla-chip">
                  First contact due
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} data-testid="crm-drawer-close" className="p-2 hover:bg-[#F3F4F6]">
            <X size={18} />
          </button>
        </div>

        {err && <div className="m-4 border border-red-200 bg-red-50 p-3 text-sm text-red-700">{err}</div>}
        {!lead ? (
          <div className="p-6 font-mono-label">Loading…</div>
        ) : (
          <div className="space-y-5 p-4">
            {/* Contact + fields */}
            <div className="border border-[#E5E7EB] p-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-[#6B7280]">Phone</span><div className="font-semibold">{lead.prospect_phone || "—"}</div></div>
                <div><span className="text-[#6B7280]">Email</span><div className="font-semibold break-all">{lead.prospect_email || "—"}</div></div>
                <div><span className="text-[#6B7280]">Attribution (commission)</span><div className="font-semibold">{lead.va_name || "Unassigned"}</div></div>
                <div><span className="text-[#6B7280]">Working owner</span><div className="font-semibold">{lead.assigned_va_name || "Unassigned"}</div></div>
                <div><span className="text-[#6B7280]">Created</span><div className="font-semibold">{(lead.created_at || "").slice(0, 10)}</div></div>
                <div><span className="text-[#6B7280]">Service (raw)</span><div className="font-semibold">{lead.service_type_raw || serviceLabel(lead.service_type)}</div></div>
              </div>
              {lead.notes && <div className="mt-2 border-t border-[#F3F4F6] pt-2 text-xs text-[#4B5563]">{lead.notes}</div>}
            </div>

            {/* Editable */}
            <div className="border border-[#E5E7EB] p-3">
              <div className="font-mono-label mb-2">Details</div>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs">
                  <span className="text-[#6B7280]">Est. value ($)</span>
                  <input type="number" min="0" data-testid="crm-edit-value" value={edit.est_value}
                    onChange={(e) => setEdit({ ...edit, est_value: e.target.value })}
                    className="mt-1 h-9 w-full border border-[#030712] px-2 text-sm" />
                </label>
                <label className="text-xs">
                  <span className="text-[#6B7280]">Next action</span>
                  <input type="date" data-testid="crm-edit-next-action" value={nextAction}
                    onChange={(e) => setNextAction(e.target.value)}
                    className="mt-1 h-9 w-full border border-[#030712] px-2 text-sm" />
                </label>
                <label className="col-span-2 text-xs">
                  <span className="text-[#6B7280]">Property address</span>
                  <input data-testid="crm-edit-address" value={edit.prospect_address}
                    onChange={(e) => setEdit({ ...edit, prospect_address: e.target.value })}
                    className="mt-1 h-9 w-full border border-[#030712] px-2 text-sm" />
                </label>
                <label className="col-span-2 text-xs">
                  <span className="text-[#6B7280]">Service type</span>
                  <select data-testid="crm-edit-service" value={edit.service_type}
                    onChange={(e) => setEdit({ ...edit, service_type: e.target.value })}
                    className="mt-1 h-9 w-full border border-[#030712] bg-white px-2 text-sm">
                    {SERVICE_TYPES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </label>
              </div>
              <button onClick={saveFields} disabled={busy} data-testid="crm-save-fields-btn"
                className="mt-3 h-9 bg-[#030712] px-4 text-xs font-semibold text-white hover:bg-[#1F2937]">
                Save details
              </button>
            </div>

            {/* Assignment (admin) */}
            {isAdmin && (
              <div className="border border-[#E5E7EB] p-3">
                <div className="font-mono-label mb-2">Assignment</div>
                <select data-testid="crm-assign-select" defaultValue=""
                  onChange={(e) => assign(e.target.value)}
                  className="h-9 w-full border border-[#030712] bg-white px-2 text-sm">
                  <option value="" disabled>{lead.assigned_va_name ? `Reassign (now: ${lead.assigned_va_name})` : "Assign to VA…"}</option>
                  {vas.map((v) => <option key={v.user_id} value={v.user_id}>{v.name}</option>)}
                </select>
                <div className="mt-1 text-[10px] text-[#6B7280]">
                  Reassignment keeps first-touch commission attribution ({lead.va_name || "none yet"}).
                </div>
                {isOwner && (
                  <div className="mt-2 border-t border-[#F3F4F6] pt-2">
                    <select data-testid="crm-attribution-override-select" defaultValue=""
                      onChange={(e) => overrideAttribution(e.target.value)}
                      className="h-9 w-full border border-red-600 bg-white px-2 text-xs text-red-700">
                      <option value="" disabled>Owner: override attribution (audit-logged)…</option>
                      {vas.map((v) => <option key={v.user_id} value={v.user_id}>{v.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Job economics (admin) — pool base for commissions */}
            {isAdmin && (
              <div className="border border-[#E5E7EB] p-3" data-testid="crm-economics-section">
                <div className="font-mono-label mb-2">Job economics &amp; commission</div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[#6B7280]">
                    Job value (revenue) $
                    <input type="number" min="0" data-testid="crm-econ-value" value={econ.job_value}
                      onChange={(e) => setEcon({ ...econ, job_value: e.target.value })}
                      className="mt-1 h-9 w-full border border-[#030712] px-2 text-sm font-normal normal-case tracking-normal" />
                  </label>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[#6B7280]">
                    Job profit $ (pool base)
                    <input type="number" min="0" data-testid="crm-econ-profit" value={econ.job_profit}
                      onChange={(e) => setEcon({ ...econ, job_profit: e.target.value })}
                      className="mt-1 h-9 w-full border border-[#030712] px-2 text-sm font-normal normal-case tracking-normal" />
                  </label>
                </div>
                {lead.job_profit == null && (
                  <div className="mt-2 border border-[#FDE68A] bg-[#FFFBEB] p-2 text-[11px] text-[#92400E]" data-testid="crm-econ-warning">
                    Job profit is missing — Trades &amp; Projects commissions pool from <b>profit</b>, so the
                    calculation stays at $0.00 until you enter it here.
                  </div>
                )}
                {econNote && (
                  <div className="mt-2 border border-[#A7F3D0] bg-[#ECFDF5] p-2 text-[11px] text-[#065F46]" data-testid="crm-econ-result">
                    {econNote.calc_notes}
                  </div>
                )}
                <button onClick={saveEconomics} disabled={busy} data-testid="crm-econ-save"
                  className="mt-2 h-9 bg-[#030712] px-4 text-xs font-semibold text-white hover:bg-[#1F2937]">
                  Save &amp; recalculate commission
                </button>
              </div>
            )}

            {/* Quotes (Phase 3 — hard gates for Quote Sent / Won) */}
            <div className="border border-[#E5E7EB] p-3" data-testid="crm-quotes-section">
              <div className="font-mono-label mb-2">Quotes</div>
              {(detail.quotes || []).length > 0 && (
                <div className="mb-3 space-y-2">
                  {detail.quotes.map((q) => (
                    <div key={q.quote_id} className="flex items-center justify-between gap-2 border border-[#F3F4F6] bg-[#F9FAFB] p-2 text-xs" data-testid={`crm-quote-${q.quote_id}`}>
                      <div>
                        <span className="font-mono font-bold">${Number(q.amount).toFixed(2)}</span>
                        <span className={`ml-2 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white ${
                          q.status === "accepted" ? "bg-emerald-700" : q.status === "declined" ? "bg-red-600" : "bg-[#D97706]"
                        }`}>{q.status}</span>
                        <div className="text-[10px] text-[#6B7280]">{(q.sent_at || "").slice(0, 10)} · {q.created_by_name}{q.description ? ` — ${q.description}` : ""}</div>
                      </div>
                      {q.status === "sent" && (
                        <div className="flex gap-1">
                          {isAdmin && (
                            <button onClick={() => setQuoteStatus(q.quote_id, "accepted")} disabled={busy}
                              data-testid={`crm-quote-accept-${q.quote_id}`}
                              className="border border-emerald-700 px-2 py-1 text-[9px] font-bold uppercase tracking-widest text-emerald-700 hover:bg-emerald-50">
                              Accept
                            </button>
                          )}
                          <button onClick={() => setQuoteStatus(q.quote_id, "declined")} disabled={busy}
                            data-testid={`crm-quote-decline-${q.quote_id}`}
                            className="border border-red-600 px-2 py-1 text-[9px] font-bold uppercase tracking-widest text-red-600 hover:bg-red-50">
                            Decline
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {lead.project_id && (
                <a href={`/ops/projects/${lead.project_id}`} data-testid="crm-project-link"
                  className="mb-2 block border border-[#0044FF] bg-blue-50 p-2 text-xs font-semibold text-[#0044FF]">
                  Linked project → {lead.project_id} (open)
                </a>
              )}
              <div className="grid grid-cols-3 gap-2">
                <input type="number" min="1" data-testid="crm-quote-amount" value={quoteAmount}
                  onChange={(e) => setQuoteAmount(e.target.value)} placeholder="Amount $"
                  className="h-9 border border-[#030712] px-2 text-sm" />
                <input data-testid="crm-quote-desc" value={quoteDesc} onChange={(e) => setQuoteDesc(e.target.value)}
                  placeholder="Scope note (optional)" className="col-span-2 h-9 border border-[#030712] px-2 text-sm" />
              </div>
              <button onClick={createQuote} disabled={busy} data-testid="crm-quote-submit"
                className="mt-2 h-9 bg-[#D97706] px-4 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#B45309]">
                Record quote sent
              </button>
              <div className="mt-1 text-[10px] text-[#6B7280]">
                Quote Sent requires a recorded quote · Won requires an accepted quote{isAdmin ? " (accepting auto-wins + creates the project)" : " (Ops accepts)"}
              </div>
            </div>

            {/* Stage actions */}
            <div className="border border-[#E5E7EB] p-3">
              <div className="font-mono-label mb-2">Move stage</div>
              <div className="flex flex-wrap gap-2">
                {stageTargets.map((s) => (
                  <button key={s.key} data-testid={`crm-stage-btn-${s.key}`}
                    onClick={() => setPendingStage(s.key)}
                    className={`border px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest hover:opacity-80 ${
                      pendingStage === s.key ? "text-white" : ""
                    }`}
                    style={pendingStage === s.key
                      ? { backgroundColor: s.color, borderColor: s.color }
                      : { borderColor: s.color, color: s.color }}>
                    {s.label}
                  </button>
                ))}
              </div>
              {pendingStage && (
                <div className="mt-3 space-y-2 border-t border-[#F3F4F6] pt-3">
                  {pendingStage === "lost" && (
                    <>
                      <select data-testid="crm-loss-reason" value={lossReason} onChange={(e) => setLossReason(e.target.value)}
                        className="h-9 w-full border border-[#030712] bg-white px-2 text-sm">
                        {LOSS_REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                      </select>
                      <input data-testid="crm-loss-note" value={lossNote} onChange={(e) => setLossNote(e.target.value)}
                        placeholder="Loss note (required for Other)"
                        className="h-9 w-full border border-[#030712] px-2 text-sm" />
                    </>
                  )}
                  <textarea data-testid="crm-stage-note" value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder={pendingStage === "contacted"
                      ? "Required: describe your first outreach (call / SMS / email)…"
                      : "Note (optional)"}
                    rows={2} className="w-full border border-[#030712] p-2 text-sm" />
                  {pendingStage === "negotiation" && !nextAction && (
                    <div className="text-[11px] text-amber-700">Negotiation requires a next-action date — set it in Details above.</div>
                  )}
                  <button onClick={() => moveStage(pendingStage)} disabled={busy} data-testid="crm-stage-confirm-btn"
                    className="h-9 w-full text-xs font-bold uppercase tracking-widest text-white"
                    style={{ backgroundColor: STAGE_MAP[pendingStage].color }}>
                    Confirm → {STAGE_MAP[pendingStage].label}
                  </button>
                </div>
              )}
            </div>

            {/* Activity log (FR-16) */}
            <div className="border border-[#E5E7EB] p-3">
              <div className="font-mono-label mb-2">Log activity</div>
              <div className="flex flex-wrap gap-1.5">
                {ACTIVITY_TYPES.map((t) => (
                  <button key={t.key} data-testid={`crm-activity-type-${t.key}`}
                    onClick={() => setActType(t.key)}
                    className={`inline-flex items-center gap-1 border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
                      actType === t.key ? "border-[#030712] bg-[#030712] text-white" : "border-[#D1D5DB] text-[#4B5563]"
                    }`}>
                    <t.icon size={12} /> {t.label}
                  </button>
                ))}
              </div>
              <textarea data-testid="crm-activity-detail" value={actDetail} onChange={(e) => setActDetail(e.target.value)}
                placeholder="What happened? (logging any outreach stops the first-contact SLA clock)"
                rows={2} className="mt-2 w-full border border-[#030712] p-2 text-sm" />
              <button onClick={logActivity} disabled={busy} data-testid="crm-activity-submit"
                className="mt-1 h-9 bg-[#030712] px-4 text-xs font-semibold text-white hover:bg-[#1F2937]">
                Log activity
              </button>
              <div className="mt-3 space-y-2 border-t border-[#F3F4F6] pt-2" data-testid="crm-activity-timeline">
                {(detail.activity || []).slice(0, 30).map((a) => (
                  <div key={a.activity_id} className="text-xs">
                    <div className="text-[#9CA3AF]">
                      <span className="font-bold uppercase tracking-widest text-[9px] text-[#4B5563]">{a.kind.replace("activity_", "").replace(/_/g, " ")}</span>
                      {" · "}{(a.created_at || "").slice(0, 16).replace("T", " ")} · {a.actor_name || a.actor_user_id}
                    </div>
                    <div className="text-[#030712]">{activityText(a)}</div>
                  </div>
                ))}
                {(detail.activity || []).length === 0 && (
                  <div className="text-xs text-[#9CA3AF]">No activity yet — log your first outreach.</div>
                )}
              </div>
            </div>

            {/* AI-drafted quick replies (Claude / Gemini / ChatGPT) */}
            <AiReplyDrafter
              leadId={leadId}
              phone={lead.prospect_phone}
            />

            {/* Stage history */}
            <div className="border border-[#E5E7EB] p-3" data-testid="crm-stage-history">
              <div className="font-mono-label mb-2 flex items-center gap-1"><Clock size={12} /> Stage history</div>
              <div className="space-y-2">
                {(detail.crm_history || []).map((h, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <StageBadge stage={h.to} />
                    <div>
                      <div className="text-[#4B5563]">
                        {(h.at || "").slice(0, 16).replace("T", " ")} · {h.by_name || h.by}
                        {h.from ? ` (from ${STAGE_MAP[h.from]?.label || h.from})` : ""}
                      </div>
                      {h.note && <div className="text-[#030712]">{h.note}</div>}
                    </div>
                  </div>
                ))}
                {(detail.crm_history || []).length === 0 && (
                  <div className="text-xs text-[#9CA3AF]">No stage moves yet.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
